import {
  Upload,
  Button,
  message,
  Progress,
  Tooltip,
  Modal,
  Popconfirm,
} from 'antd';
import {
  UploadOutlined,
  CloseOutlined,
  CopyOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useState, useEffect, useContext } from 'react';
import { post } from '~/Services';
import { getCurrentModule } from '~/Utils/StringConstants';
import { AuthContext } from '~/Context/AuthContext';

const { Dragger } = Upload;

export const GlobalFileUpload = () => {
  const { user } = useContext(AuthContext);
  const currentModule = getCurrentModule();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploaded, setUploaded] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const reset = () => {
    if (file || previewUrl) {
      setFile(null);
      setUploaded(null);
      setProgress(0);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
    }
  };

  const onDraggerChange = (info: any) => {
    const { file: rcFile, fileList } = info;

    // If fileList is empty, user removed the file
    if (fileList.length === 0) {
      reset();
      return;
    }

    // Only process if we have an actual file object
    if (!rcFile.originFileObj) {
      return;
    }

    const selected = rcFile.originFileObj;
    
    const validTypes = ['image/', 'video/', 'application/pdf'];
    const isValidType = validTypes.some(type => selected.type.startsWith(type));
    
    if (!isValidType) {
      message.error('Please upload an image, video, or PDF file');
      return;
    }

    const generatePreview = (file: any) => {
      // if (file.type.startsWith('image/')) {
      //   const reader = new FileReader();
      //   reader.onload = () => {
      //     setPreviewUrl(reader.result);
      //     setFile(selected);
      //   };
      //   reader.onerror = () => {
      //     console.error('Failed to read file');
      //     setPreviewUrl(null);
      //     setFile(null);
      //   };
      //   reader.readAsDataURL(file);
      // } else {
      //   const objUrl = URL.createObjectURL(file);
      //   setPreviewUrl(objUrl);
      //   setFile(selected);
      // }
      const objUrl = URL.createObjectURL(file);
      setPreviewUrl(objUrl);
      setFile(selected);
    };


    // Only generate preview if file has changed
    if (!file || file.name !== selected.name || file.size !== selected.size) {
      generatePreview(selected);
    }
  };

  const uploadFile = async () => {
    if (!file) return;

    const formData = new FormData();
    formData.append('upload-file', file);
    formData.append('application', 'CORPORATE');

    setUploading(true);
    setProgress(0);

    try {
      const res: any = await post("management/upload-file", formData);
      
      if (res && res.response && res.response.data) {
        const uploadedFile = {
          url: res.response.data,
          fileType: file.type,
          name: file.name
        };
        
        setUploaded(uploadedFile);
        message.success('File uploaded successfully!');
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err: any) {
      console.error('Upload error:', err);
      message.error(err?.response?.data?.message ?? 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const copyUrl = () => {
    if (!uploaded?.url) return;
    navigator.clipboard.writeText(uploaded.url).then(
      () => message.success('URL copied!'),
      () => message.error('Copy failed')
    );
  };

  const fileKind = (type: any) => {
    if (type?.startsWith('image/')) return 'image';
    if (type?.startsWith('video/')) return 'video';
    if (type === 'application/pdf') return 'pdf';
    return 'generic';
  };

  // Only show if user is logged in and current module is CORPORATE
  if (!user || currentModule?.toUpperCase() !== 'CORPORATE') {
    return null;
  }

  return (
    <div className="fixed bottom-[1.5rem] right-[1.5rem] z-[1000]">
      {!isOpen && (
        <div
          className="w-[60px] h-[60px] text-white bg-[#712b90] rounded-full flex items-center justify-center cursor-pointer shadow-[0_4px_12px_rgba(0,0,0,0.15)] transition-all duration-300 ease-[cubic-bezier(0.645,0.045,0.355,1)] hover:bg-[#5a2173] hover:scale-105"
          onClick={() => setIsOpen(true)}
        >
          <UploadOutlined className="text-[24px] text-white" style={{ strokeWidth: 2 }} />
        </div>
      )}

      {isOpen && (
        <div className="w-[500px] max-h-[80vh] bg-white rounded-lg shadow-[0_4px_20px_rgba(0,0,0,0.1)] overflow-hidden animate-[slide-up_0.3s_ease] border border-[#e8e8e8] m-0 p-0 flex flex-col">
          <style>{`
            @keyframes slide-up {
              from {
                transform: translateY(20px);
                opacity: 0;
              }
              to {
                transform: translateY(0);
                opacity: 1;
              }
            }
          `}</style>
          
          <div className="flex justify-between items-center px-5 py-3.5 bg-[#f9f0ff] border-b border-[#e8d6f7] font-semibold text-[#4a1a5e] text-[15px] flex-shrink-0">
            <span>Upload File</span>
            {/* <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={() => {
                reset();
                setIsOpen(false);
              }}
              className="text-[#7b1fa2] hover:text-[#4a1a5e] transition-colors duration-200"
            /> */}
          </div>

          <div className="flex-1 overflow-y-auto overflow-x-hidden">
          {!uploaded && (
            <>
              <div className="m-5">
                <Dragger
                  className="!p-8 !bg-[#fcf9ff] !border-2 !border-dashed !border-[#d1c4e9] !rounded-lg hover:!border-[#9c27b0] hover:!bg-[#f9f0ff] transition-all duration-300"
                  multiple={false}
                  showUploadList={false}
                  beforeUpload={(file) => {
                    const isAcceptable = [
                      "image/jpeg",
                      "image/png",
                      "image/gif",
                      "image/webp",
                      "video/mp4",
                      "video/webm",
                      "video/quicktime",
                      "application/pdf",
                    ].includes(file.type);

                    if (!isAcceptable) {
                      message.error(
                        "Please upload an image (JPEG, PNG, GIF, WebP), video (MP4, WebM, MOV), or PDF file"
                      );
                    }
                    return isAcceptable || Upload.LIST_IGNORE;
                  }}
                  accept="image/*,video/*,.pdf"
                  onChange={onDraggerChange}
                  disabled={uploading}
                >
                  <div className="py-2 text-center">
                    <p className="ant-upload-drag-icon">
                      <UploadOutlined className="text-[48px] text-[#722c91]" />
                    </p>
                    <p className="my-3 mx-0 mt-3 mb-1.5 text-[#4a1a5e] text-[15px] font-medium whitespace-nowrap overflow-hidden text-ellipsis">
                      {file ? file.name : "Click or drag file to upload"}
                    </p>
                    <p className="text-[#7b7a8c] text-[13px] leading-[1.5]">
                      Images • Videos • PDFs
                    </p>
                  </div>
                </Dragger>
              </div>

              {/* ✅ Show file preview before upload */}
              {previewUrl && (
                <div className="px-5 pb-4 text-center">
                  {file?.type.startsWith("image/") && (
                    <img
                      src={previewUrl}
                      alt="preview"
                      className="max-w-full max-h-[250px] rounded-lg object-contain shadow-[0_2px_8px_rgba(0,0,0,0.1)] mx-auto"
                    />
                  )}
                  {file?.type.startsWith("video/") && (
                    <video
                      controls
                      className="w-full max-h-[250px] rounded-lg bg-black mx-auto"
                      src={previewUrl}
                    >
                      Your browser does not support the video tag.
                    </video>
                  )}
                  {file?.type === "application/pdf" && (
                    <iframe
                      src={previewUrl}
                      title="PDF preview"
                      className="w-full h-[300px] border-none rounded-lg"
                    />
                  )}
                </div>
              )}

              {uploading && (
                <div className="px-5 pb-3">
                  <Progress percent={progress} strokeColor="#722c91" />
                </div>
              )}
            </>
          )}

          {uploaded && (
            <div className="p-5 text-center">
              {(() => {
                const kind = fileKind(uploaded.fileType);
                if (kind === 'image' && previewUrl) {
                  return (
                    <img 
                      src={previewUrl} 
                      alt="preview" 
                      className="max-w-full max-h-[300px] rounded-lg object-contain shadow-[0_2px_8px_rgba(0,0,0,0.1)]"
                    />
                  );
                }
                if (kind === 'video' && previewUrl) {
                  return (
                    <video 
                      controls 
                      className="w-full max-h-[300px] rounded-lg bg-black" 
                      src={previewUrl}
                    >
                      Your browser does not support the video tag.
                    </video>
                  );
                }
                if (kind === 'pdf' && previewUrl) {
                  return (
                    <iframe
                      src={previewUrl}
                      title="PDF preview"
                      className="w-full h-[340px] border-none rounded-lg"
                    />
                  );
                }
                return (
                  <div className="p-5 text-[#7b1fa2]">
                    <div className="text-[48px] mb-2">File</div>
                    <p>{file?.name}</p>
                  </div>
                );
              })()}

              <div className="mt-4 text-left">
                <strong>URL:</strong>{' '}
                <a 
                  href={uploaded.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline break-all"
                >
                  {uploaded.url}
                </a>
              </div>

              <div className="mt-4 flex justify-center gap-2">
                <Tooltip title="Copy URL">
                  <Button icon={<CopyOutlined />} size="small" onClick={copyUrl} />
                </Tooltip>
              </div>
            </div>
          )}
          </div>

          <div className="flex justify-end p-4 border-t border-[#f0e5ff] gap-3 bg-[#fcf9ff] w-full box-border flex-shrink-0">
            {!uploaded?.url && (
              <Popconfirm
                title="If you close now, your uploaded file will be removed. Do you want to continue?"
                okText="Yes"
                cancelText="No"
                okButtonProps={{
                  className: "!bg-[#712b90] hover:!bg-[#5a2173] border-none",
                }}
                onConfirm={() => {
                  reset();
                  setIsOpen(false);
                }}
              >
                <Button
                  disabled={uploading}
                  className="border border-[#d1c4e9] text-[#5e35b1] bg-white hover:!border-[#9c27b0] hover:!text-[#4a1a5e] hover:!bg-[#f9f0ff]"
                >
                  Close
                </Button>
              </Popconfirm>)}

            {!uploaded && (
              <Button
                type="primary"
                onClick={uploadFile}
                loading={uploading}
                disabled={!file || uploading}
                className="min-w-[100px] h-9 font-medium !bg-[#712b90] border-none hover:!bg-[#5a2173] hover:-translate-y-px transition-all duration-200 disabled:!bg-[#d1c4e9] disabled:!text-white disabled:cursor-not-allowed disabled:transform-none"
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            )}

            {uploaded && (
              <Button 
                type="primary" 
                onClick={() => {
                  reset();
                  setIsOpen(false);
                }}
                className="min-w-[100px] h-9 font-medium !bg-[#712b90] border-none hover:!bg-[#5a2173]"
              >
                Done
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
